import { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { ArrowLeft, ArrowRight, Plus, Stars02 } from "@untitledui/icons"
import type { VehicleSide } from "@/lib/api/types"
import { cx } from "@/lib/utils/cx"
import { QE_SIDES_COPY } from "./copy"
import { PhotoEditor } from "./photo-editor"
import { PhotoTile } from "./photo-tile"
import {
  basicSideOf,
  shortSideLabel,
  SIDE_OPTIONS,
  type BasicSide,
  type QEPhoto,
} from "./types"
import type { QuickEstimateActions } from "./use-quick-estimate-state"
import type { UseQECapture } from "./use-qe-capture"

// Quick Estimate 3 — Step 3 (AI side tags review).
//
// Photos arrive here already filtered for submittability (the photos
// step blocks Continue until at least one passes quality / is
// kept-without-tag). This step's only job is "did the AI label each
// shot's angle correctly?" — tap any tile to correct via the side
// picker. AI confidence is implicit: the user reviews, the AI doesn't
// gate.
//
// Coverage strip below the activity chip surfaces *which families*
// (front, rear, sides, corners, roof) currently have at least one
// shot. It's a soft hint, never a blocker — the advisor can ship
// 1 photo if they want.

interface StepSidesProps {
  photos: QEPhoto[]
  actions: QuickEstimateActions
  capture: UseQECapture
  onBack: () => void
  onContinue: () => void
  /** Overrides eyebrow when this step is reused inside a wizard with
   *  a different total step count (e.g. full-estimate's 6 steps). */
  eyebrowLabel?: string
}

// Display order for the coverage strip — front-first matches the
// picker order so user mental-model is consistent across surfaces.
const SIDE_DISPLAY_ORDER: BasicSide[] = SIDE_OPTIONS.map((o) => o.value)

function sideLabelOf(side: BasicSide): string {
  return SIDE_OPTIONS.find((o) => o.value === side)?.label ?? "—"
}

function sideNounOf(side: BasicSide): string {
  return SIDE_OPTIONS.find((o) => o.value === side)?.noun ?? "another"
}

export function StepSides({
  photos,
  actions,
  capture,
  onBack,
  onContinue,
  eyebrowLabel,
}: StepSidesProps) {
  const [editingId, setEditingId] = useState<string | null>(null)

  // Only submittable photos make it onto this step. Photos that are
  // still eval-failed without a "use without tag" decision live on
  // the photos step until the user resolves them.
  const visiblePhotos = useMemo(
    () => photos.filter((p) => p.uploadedUrl.length > 0 || p.sendWithoutAiTag),
    [photos],
  )

  const editingPhoto = useMemo(
    () => visiblePhotos.find((p) => p.id === editingId) ?? null,
    [visiblePhotos, editingId],
  )

  useEffect(() => {
    if (editingId && !editingPhoto) setEditingId(null)
  }, [editingId, editingPhoto])

  const handleChangeSide = (photo: QEPhoto, side: VehicleSide) => {
    actions.updatePhoto(photo.id, {
      detectedSide: side,
      sideOverridden: true,
      // Manually tagging an untagged photo flips it back to "tagged"
      // — the user has now told us the side, so it's no longer in
      // the "send without tag" state.
      sendWithoutAiTag: false,
    })
  }

  const handleChangeDescription = (photo: QEPhoto, text: string) => {
    actions.updatePhoto(photo.id, { description: text })
  }

  const handleRemove = (photo: QEPhoto) => {
    capture.forgetFile(photo.id)
    actions.removePhoto(photo.id)
    setEditingId((curr) => (curr === photo.id ? null : curr))
  }

  // Live tag summary chip (mirror of photos step's activity chip).
  const inflightCount = capture.inflight.length
  const taggedSides: string[] = []
  let editedCount = 0
  for (const p of visiblePhotos) {
    if (p.sendWithoutAiTag) continue
    const tag = shortSideLabel(p.detectedSide)
    if (taggedSides[taggedSides.length - 1] !== tag) taggedSides.push(tag)
    if (p.sideOverridden) editedCount++
  }

  const summaryText =
    inflightCount > 0
      ? `AI reviewing ${inflightCount}…`
      : visiblePhotos.length === 0
        ? QE_SIDES_COPY.aiTaggedNone
        : QE_SIDES_COPY.aiTaggedSummaryEdited(taggedSides, editedCount)

  // Coverage: which basic sides have at least one tagged photo.
  const coveredSides = new Set<BasicSide>()
  for (const p of visiblePhotos) {
    if (p.sendWithoutAiTag) continue
    coveredSides.add(basicSideOf(p.detectedSide))
  }
  const missingSides = SIDE_DISPLAY_ORDER.filter((s) => !coveredSides.has(s))

  const canContinue = visiblePhotos.length > 0 && inflightCount === 0

  return (
    <div className="space-y-6 pb-36 sm:pb-0">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
          {eyebrowLabel ?? QE_SIDES_COPY.eyebrow}
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {QE_SIDES_COPY.title}
        </h2>
        <p className="mt-1.5 text-[14px] text-zinc-500 leading-relaxed">
          {QE_SIDES_COPY.subtitle}
        </p>
      </header>

      <SummaryChip text={summaryText} hasInflight={inflightCount > 0} />

      {visiblePhotos.length > 0 && (
        <CoverageStrip covered={coveredSides} missing={missingSides} />
      )}

      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <AnimatePresence initial={false} mode="popLayout">
            {visiblePhotos.map((photo) => (
              <motion.div
                key={`shot-${photo.id}`}
                layout
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ type: "spring", stiffness: 320, damping: 26 }}
              >
                <PhotoTile
                  photo={photo}
                  onOpen={() => setEditingId(photo.id)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Add another — full-width, labeled. Kicks off the same
            preflight + concurrency + retry pipeline as the photos
            step. The new photo lands here once it's evaluated. */}
        <button
          type="button"
          onClick={capture.triggerCapture}
          className="w-full rounded-2xl border border-zinc-200 hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors duration-150 h-12 flex items-center justify-center gap-2 text-[13.5px] font-semibold text-zinc-700"
        >
          <Plus className="size-4" aria-hidden />
          {QE_SIDES_COPY.addMore}
        </button>
      </div>

      {/* CTA bar */}
      <div
        className={cx(
          "space-y-2",
          "fixed inset-x-0 bottom-0 z-30",
          "px-4 pt-3 pb-[max(env(safe-area-inset-bottom),12px)]",
          "bg-gradient-to-t from-white via-white/95 to-white/0",
          "sm:static sm:px-0 sm:pt-2 sm:pb-0 sm:bg-transparent",
        )}
      >
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          className={cx(
            "w-full h-14 rounded-2xl text-[15px] font-semibold flex items-center justify-center gap-2 transition-all duration-150",
            canContinue
              ? "bg-zinc-900 hover:bg-black text-white shadow-lg shadow-zinc-900/15 hover:shadow-zinc-900/25"
              : "bg-zinc-100 text-zinc-400 cursor-not-allowed",
          )}
        >
          {canContinue
            ? QE_SIDES_COPY.continue
            : QE_SIDES_COPY.continueDisabled}
          {canContinue && <ArrowRight className="size-4" aria-hidden />}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="w-full h-10 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {QE_SIDES_COPY.back}
        </button>
      </div>

      <PhotoEditor
        open={!!editingPhoto}
        photo={editingPhoto}
        canRetry={false}
        retrying={false}
        onClose={() => setEditingId(null)}
        onChangeSide={(side) =>
          editingPhoto && handleChangeSide(editingPhoto, side)
        }
        onChangeDescription={(text) =>
          editingPhoto && handleChangeDescription(editingPhoto, text)
        }
        onRemove={() => editingPhoto && handleRemove(editingPhoto)}
      />
    </div>
  )
}

/* ── Summary chip ─────────────────────────────────────────────── */

function SummaryChip({
  text,
  hasInflight,
}: {
  text: string
  hasInflight: boolean
}) {
  return (
    <div className="flex items-center justify-center">
      <motion.div
        layout
        className={cx(
          "inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full",
          "bg-indigo-50/70 border border-indigo-100",
          "text-[12px] font-semibold text-indigo-900",
        )}
      >
        <Stars02 className="size-3.5 text-indigo-600" aria-hidden />
        <span className="relative flex size-1.5">
          {hasInflight && (
            <motion.span
              className="absolute inline-flex h-full w-full rounded-full bg-indigo-500 opacity-75"
              animate={{ scale: [1, 2.4, 1], opacity: [0.7, 0, 0.7] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeOut" }}
            />
          )}
          <span className="relative inline-flex rounded-full size-1.5 bg-indigo-500" />
        </span>
        <span className="leading-none">{text}</span>
      </motion.div>
    </div>
  )
}

/* ── Coverage strip ───────────────────────────────────────────── */

function CoverageStrip({
  covered,
  missing,
}: {
  covered: Set<BasicSide>
  missing: BasicSide[]
}) {
  const hint =
    missing.length === 0
      ? "Looks comprehensive."
      : missing.length === 1
        ? `Consider a ${sideNounOf(missing[0])} shot.`
        : `Add ${missing.length} more angle${missing.length === 1 ? "" : "s"}.`

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white/60 px-3.5 py-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
          {QE_SIDES_COPY.coverageEyebrow}
        </p>
        <p className="text-[11.5px] text-zinc-500 leading-tight truncate">
          {hint}
        </p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {SIDE_DISPLAY_ORDER.map((s) => {
          const has = covered.has(s)
          return (
            <span
              key={s}
              className={cx(
                "inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11.5px] font-semibold",
                has
                  ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                  : "bg-zinc-50 text-zinc-500 border border-zinc-200",
              )}
            >
              <span
                className={cx(
                  "size-1.5 rounded-full",
                  has ? "bg-emerald-500" : "bg-zinc-300",
                )}
                aria-hidden
              />
              {sideLabelOf(s)}
            </span>
          )
        })}
      </div>
    </div>
  )
}

